import { Component, OnInit, OnDestroy } from "@angular/core";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";

interface CreateSecurityGroupRuleRow {
  id: string;
  protocol: string;
  port: string;
  target: string;
  description: string;
}

type SecurityGroupRuleTargetType =
  | "cidr_ipv4"
  | "cidr_ipv6"
  | "referenced_group_ids"
  | "prefix_list_ids";

interface SecurityGroupRulePayloadRow {
  protocol: string;
  from_port: number | null;
  to_port: number | null;
  target_type: SecurityGroupRuleTargetType;
  target_value: string;
}

interface SecurityGroupApiRulePayload {
  ip_protocol: string;
  from_port?: number;
  to_port?: number;
  cidr_ipv4: string[];
  cidr_ipv6: string[];
  prefix_list_ids: string[];
  referenced_group_ids: string[];
}

interface CreateSecurityGroupRulesUpdatePayload {
  account_id: string;
  region: string;
  authorize_ingress: SecurityGroupApiRulePayload[];
  revoke_ingress: SecurityGroupApiRulePayload[];
  authorize_egress: SecurityGroupApiRulePayload[];
  revoke_egress: SecurityGroupApiRulePayload[];
}

interface CreateSecurityGroupFormModel {
  group_name: string;
  description: string;
  vpc_id: string;
  environment: string;
  inbound_rules: CreateSecurityGroupRuleRow[];
  outbound_rules: CreateSecurityGroupRuleRow[];
}

@Component({
  selector: "app-launch-ec2-operations",
  templateUrl: "./launch-ec2.operations.component.html",
  styleUrls: ["./launch-ec2.operations.component.css"],
})
export class LaunchEc2OperationsComponent implements OnInit, OnDestroy {
  private role: string = (localStorage.getItem("role") || "").toLowerCase();
  urlPrefix: string = this.role == "admin" ? "admin" : "client";
  apiBaseUrl: string = environment.apiURL || APIService.API_ENDPOINTV3;
  accountId: any = "";
  regionId: any = "";
  currentMessage: Subscription;
  private amisRequestSeq: number = 0;
  private iamRolesRequestSeq: number = 0;
  iamRolesNextToken: string = "";
  showCreateSecurityGroupForm: boolean = false;
  createSecurityGroupSubmitAttempted: boolean = false;
  createSecurityGroupStep: number = 1;
  createSecurityGroupForm: CreateSecurityGroupFormModel =
    this.getDefaultCreateSecurityGroupForm();
  readonly createSecurityGroupStepLabels: string[] = [
    "Basic",
    "Inbound",
    "Outbound",
    "Tags",
    "Review",
  ];
  readonly createSecurityGroupProtocolOptions: string[] = ["TCP", "UDP", "ICMP", "All"];
  readonly createSecurityGroupEnvironmentOptions: string[] = [
    "prod",
    "dev",
    "qa",
    "uat",
    "stage",
  ];
  private createSecurityGroupRuleSequence: number = 0;

  loading: any = {};
  step: number = 1;
  options: any = {
    os: [],
    amis: [],
    instanceTypes: [],
    keys: [],
    vpcs: [],
    subnets: [],
    securityGroups: [],
    iamRoles: [],
  };

  form: any = {
    operating_system: null,
    ami_id: null,
    instance_type: null,
    key_name: null,
    vpc_id: null,
    subnet_id: null,
    security_group_ids: [],
    associate_public_ip_address: null,
    block_device_mappings: [
      {
        device_name: "",
        ebs: { volume_size: null, volume_type: null, delete_on_termination: null },
      },
    ],
    instance_name: "",
    tags: [{ key: "", value: "" }],
    min_count: 1,
    max_count: 1,
    metadata_options: { http_tokens: null },
    monitoring: { enabled: null },
    placement: { tenancy: null },
    ebs_optimized: null,
    instance_initiated_shutdown_behavior: null,
    architecture: null,
    user_data: "",
    ec2_launch_justification: "",
    iam_instance_profile_arn: null,
  };

  constructor(
    private apiServer: APIService,
    private notifier: NotifierService,
    private http: HttpClient
  ) {}

  private normalizeContextValue(value: any): string {
    const v = (value ?? "").toString().trim();
    if (!v || v.toLowerCase() == "null" || v.toLowerCase() == "undefined") {
      return "";
    }
    return v;
  }

  private resetFormOnContextChange() {
    this.step = 1;
    this.amisRequestSeq++;
    this.iamRolesRequestSeq++;
    this.iamRolesNextToken = "";
    this.showCreateSecurityGroupForm = false;
    this.createSecurityGroupSubmitAttempted = false;
    this.createSecurityGroupStep = 1;
    this.createSecurityGroupForm = this.getDefaultCreateSecurityGroupForm();

    this.form = {
      operating_system: null,
      ami_id: null,
      instance_type: null,
      key_name: null,
      vpc_id: null,
      subnet_id: null,
      security_group_ids: [],
      associate_public_ip_address: null,
      block_device_mappings: [
        {
          device_name: "",
          ebs: { volume_size: null, volume_type: null, delete_on_termination: null },
        },
      ],
      instance_name: "",
      tags: [{ key: "", value: "" }],
      min_count: 1,
      max_count: 1,
      metadata_options: { http_tokens: null },
      monitoring: { enabled: null },
      placement: { tenancy: null },
      ebs_optimized: null,
      instance_initiated_shutdown_behavior: null,
      architecture: null,
      user_data: "",
      ec2_launch_justification: "",
      iam_instance_profile_arn: null,
    };

    this.options = {
      os: [],
      amis: [],
      instanceTypes: [],
      keys: [],
      vpcs: [],
      subnets: [],
      securityGroups: [],
      iamRoles: [],
    };
  }

  ngOnInit(): void {
    this.accountId = this.normalizeContextValue(localStorage.getItem("accountId"));
    this.regionId = this.normalizeContextValue(localStorage.getItem("regionId"));
    this.createSecurityGroupForm = this.getDefaultCreateSecurityGroupForm();

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (!msg) return;
      try {
        let d = JSON.parse(msg);
        if (d.value == null || d.source != "notifier") return;
        if (d.key == "accountId") {
          const nextAccountId = this.normalizeContextValue(d.value);
          if (nextAccountId === this.accountId) return;
          this.accountId = nextAccountId;
          this.resetFormOnContextChange();
          this.loadStaticMasters();
        } else if (d.key == "regionId") {
          const nextRegionId = this.normalizeContextValue(d.value);
          if (nextRegionId === this.regionId) return;
          this.regionId = nextRegionId;
          this.resetFormOnContextChange();
          this.loadStaticMasters();
        }
      } catch {}
    });

    if (this.accountId && this.regionId) {
      this.loadStaticMasters();
    }
  }

  ngOnDestroy(): void {
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }

  headers() {
    return { Authorization: localStorage.getItem("t") };
  }

  private getPostHeaders() {
    let headers = new HttpHeaders().set(
      "Content-Type",
      "application/json; charset=utf-8"
    );
    const token = localStorage.getItem("t");
    if (token) {
      headers = headers.set("Authorization", token);
    }
    return headers;
  }

  private getPutHeaders() {
    let headers = new HttpHeaders().set(
      "Content-Type",
      "application/json; charset=utf-8"
    );
    const token = localStorage.getItem("t");
    if (token) {
      headers = headers.set("Authorization", token);
    }
    return headers;
  }

  private buildApiUrl(prefix: string, path: string) {
    return `${this.apiBaseUrl}/${prefix}/${path}`;
  }

  private wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async getWithRetry(path: string, maxAttempts: number = 3) {
    let attempt = 0;
    while (attempt < maxAttempts) {
      const result = await this.getWithPrefixFallback(path);
      if (result) {
        return result;
      }
      attempt++;
      if (attempt < maxAttempts) {
        await this.wait(400 * attempt);
      }
    }
    return null;
  }

  async get(url: string): Promise<any> {
    try {
      const r = await this.apiServer.getDataPromis(url, {}, this.headers());
      if (typeof r == "string") return null;
      return r;
    } catch {
      return null;
    }
  }

  async post(url: string, body: any): Promise<any> {
    try {
      const r = await this.http
        .post<any>(url, body, { headers: this.getPostHeaders() })
        .toPromise();
      if (!r || typeof r == "string") return null;
      return r;
    } catch {
      return null;
    }
  }

  async put(url: string, body: any): Promise<any> {
    try {
      return await this.http
        .put<any>(url, body, { headers: this.getPutHeaders() })
        .toPromise();
    } catch (error: any) {
      return error?.error || null;
    }
  }

  async getWithPrefixFallback(path: string): Promise<any> {
    const primary = this.urlPrefix;
    return await this.get(this.buildApiUrl(primary, path));
  }

  async postWithPrefixFallback(path: string, body: any): Promise<any> {
    const primary = this.urlPrefix;
    return await this.post(this.buildApiUrl(primary, path), body);
  }

  async putWithPrefixFallback(path: string, body: any): Promise<any> {
    const primary = this.urlPrefix;
    return await this.put(this.buildApiUrl(primary, path), body);
  }

  private isSuccessResponse(response: any): boolean {
    return !!(
      response?.status == "1" ||
      response?.s == "1" ||
      response?.group_id ||
      response?.security_group_id ||
      response?.data?.group_id
    );
  }

  private createSecurityGroupRule(
    direction: "inbound" | "outbound",
    overrides: Partial<CreateSecurityGroupRuleRow> = {}
  ): CreateSecurityGroupRuleRow {
    this.createSecurityGroupRuleSequence += 1;

    const defaults =
      direction === "inbound"
        ? {
            protocol: "TCP",
            port: "443",
            target: "0.0.0.0/0",
            description: "HTTPS",
          }
        : {
            protocol: "All",
            port: "-",
            target: "0.0.0.0/0",
            description: "Allow all outbound",
          };

    return {
      id: `sg-rule-${direction}-${this.createSecurityGroupRuleSequence}`,
      ...defaults,
      ...overrides,
    };
  }

  private getDefaultCreateSecurityGroupForm(vpcId: string = ""): CreateSecurityGroupFormModel {
    return {
      group_name: "",
      description: "",
      vpc_id: vpcId || this.form?.vpc_id || "",
      environment: "prod",
      inbound_rules: [this.createSecurityGroupRule("inbound")],
      outbound_rules: [this.createSecurityGroupRule("outbound")],
    };
  }

  async loadStaticMasters() {
    await Promise.all([
      this.loadOperatingSystems(),
      this.loadInstanceTypes(),
      this.loadKeyPairs(),
      this.loadVpcs(),
      this.loadIamRoles(),
    ]);
  }

  async loadOperatingSystems() {
    if (!this.accountId || !this.regionId) {
      return;
    }
    this.loading.os = true;
    const path = `apis/ec2-get-master-data/ec2/operating-systems?account_id=${this.accountId}&region=${this.regionId}`;
    const r = await this.getWithPrefixFallback(path);
    this.options.os = r?.operating_systems || [];
    this.loading.os = false;
  }

  async loadAmis() {
    const requestId = ++this.amisRequestSeq;
    const selectedOs = this.form.operating_system;
    this.form.ami_id = null;
    this.options.amis = [];
    if (!selectedOs) {
      this.loading.amis = false;
      return;
    }
    this.loading.amis = true;
    const os = encodeURIComponent(selectedOs);
    const path = `apis/ec2-get-master-data/ec2/amis?account_id=${this.accountId}&region=${this.regionId}&operating_system=${os}`;
    const r = await this.getWithRetry(path, 3);
    if (requestId !== this.amisRequestSeq || this.form.operating_system !== selectedOs) {
      return;
    }
    this.options.amis = r?.items || [];
    this.loading.amis = false;
  }

  async loadInstanceTypes() {
    this.loading.instanceTypes = true;
    const path = `apis/ec2-get-master-data/ec2/instance-types?account_id=${this.accountId}&region=${this.regionId}`;
    const r = await this.getWithRetry(path, 3);
    this.options.instanceTypes = r?.items || [];
    this.loading.instanceTypes = false;
  }

  async loadKeyPairs() {
    this.loading.keys = true;
    const path = `apis/ec2-get-master-data/ec2/key-pairs?account_id=${this.accountId}&region=${this.regionId}`;
    const r = await this.getWithRetry(path, 3);
    this.options.keys = r?.items || [];
    this.loading.keys = false;
  }

  async loadVpcs() {
    this.loading.vpcs = true;
    const path = `apis/ec2-config-2/api/ec2/vpcs?account_id=${this.accountId}&region=${this.regionId}`;
    const r = await this.getWithPrefixFallback(path);
    this.options.vpcs = r?.vpcs || [];
    this.loading.vpcs = false;
  }

  async onVpcChange() {
    this.form.subnet_id = null;
    this.form.security_group_ids = [];
    this.options.subnets = [];
    this.options.securityGroups = [];
    if (this.showCreateSecurityGroupForm && !this.createSecurityGroupForm?.vpc_id) {
      this.createSecurityGroupForm.vpc_id = this.form.vpc_id || "";
    }
    if (!this.form.vpc_id) return;
    await Promise.all([this.loadSubnets(), this.loadSecurityGroups()]);
  }

  async loadSubnets() {
    this.loading.subnets = true;
    const path = `apis/ec2-config-2/api/ec2/subnets?account_id=${this.accountId}&region=${this.regionId}&vpc_id=${this.form.vpc_id}`;
    const r = await this.getWithPrefixFallback(path);
    this.options.subnets = r?.subnets || [];
    this.loading.subnets = false;
  }

  async loadSecurityGroups() {
    this.loading.securityGroups = true;
    const path = `apis/ec2-config-2/api/ec2/security-groups?account_id=${this.accountId}&region=${this.regionId}&vpc_id=${this.form.vpc_id}`;
    const r = await this.getWithPrefixFallback(path);
    this.options.securityGroups = r?.security_groups || [];
    this.loading.securityGroups = false;
  }

  async loadIamRoles(nextToken: any = null, requestId: number = 0) {
    const isFirstPage = !nextToken;
    if (isFirstPage) {
      requestId = ++this.iamRolesRequestSeq;
      this.options.iamRoles = [];
      this.iamRolesNextToken = "";
    } else if (!requestId) {
      requestId = this.iamRolesRequestSeq;
    }

    if (requestId !== this.iamRolesRequestSeq) {
      return;
    }
    this.loading.iamRoles = true;

    let path = `apis/ec2-get-master-data/ec2/iam-roles?account_id=${this.accountId}&page_size=10`;
    if (nextToken) path += `&next_token=${encodeURIComponent(nextToken)}`;
    const r = await this.getWithRetry(path, 3);
    if (requestId !== this.iamRolesRequestSeq) {
      return;
    }

    if (!r) {
      this.loading.iamRoles = false;
      this.notifier.alert(
        "Warning",
        "",
        "IAM roles loading timed out. Some roles may be missing. Please refresh.",
        "warning",
        5000
      );
      return;
    }

    this.options.iamRoles = [...this.options.iamRoles, ...(r?.items || [])];
    this.iamRolesNextToken = r?.next_token || "";
    this.loading.iamRoles = false;
  }

  async loadMoreIamRoles(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.iamRolesNextToken || this.loading.iamRoles) return;
    await this.loadIamRoles(this.iamRolesNextToken, this.iamRolesRequestSeq);
  }

  canProceedToPricing(): boolean {
    const hasTags =
      Array.isArray(this.form?.tags) &&
      this.form.tags.length > 0 &&
      this.form.tags.every(
        (t: any) =>
          (t?.key ?? "").toString().trim() !== "" &&
          (t?.value ?? "").toString().trim() !== ""
      );

    return !!(
      (this.form?.operating_system ?? "").toString().trim() &&
      (this.form?.ami_id ?? "").toString().trim() &&
      (this.form?.instance_type ?? "").toString().trim() &&
      (this.form?.key_name ?? "").toString().trim() &&
      (this.form?.vpc_id ?? "").toString().trim() &&
      (this.form?.subnet_id ?? "").toString().trim() &&
      Array.isArray(this.form?.security_group_ids) &&
      this.form.security_group_ids.length > 0 &&
      this.form?.associate_public_ip_address !== null &&
      this.form?.associate_public_ip_address !== undefined &&
      (this.form?.ec2_launch_justification ?? "").toString().trim() &&
      (this.form?.instance_name ?? "").toString().trim() &&
      hasTags &&
      (this.form?.metadata_options?.http_tokens ?? "").toString().trim() &&
      this.form?.monitoring?.enabled !== null &&
      this.form?.monitoring?.enabled !== undefined &&
      (this.form?.placement?.tenancy ?? "").toString().trim() &&
      this.form?.ebs_optimized !== null &&
      this.form?.ebs_optimized !== undefined &&
      (this.form?.instance_initiated_shutdown_behavior ?? "").toString().trim() &&
      (this.form?.architecture ?? "").toString().trim() &&
      (this.form?.iam_instance_profile_arn ?? "").toString().trim()
    );
  }

  async goToPricingStep() {
    if (!this.canProceedToPricing()) {
      this.notifier.alert(
        "Warning",
        "",
        "Please fill all required fields before proceeding.",
        "warning",
        3000
      );
      return;
    }
    this.step = 2;
  }

  goToFormStep() {
    this.step = 1;
  }

  private getSelectedAmi() {
    return this.options.amis.find((a: any) => a.ami_id === this.form.ami_id) || {};
  }

  private getRequesterId(): string {
    return this.normalizeContextValue(localStorage.getItem("eId"));
  }

  buildSaveRequestBody() {
    const selectedAmi = this.getSelectedAmi();
    const mappings = Array.isArray(this.form.block_device_mappings)
      ? this.form.block_device_mappings
          .map((m: any) => ({
            device_name: (m?.device_name ?? "").toString().trim(),
            ebs: {
              volume_size: m?.ebs?.volume_size ?? null,
              volume_type: m?.ebs?.volume_type ?? null,
              delete_on_termination: m?.ebs?.delete_on_termination ?? null,
            },
          }))
          .filter(
            (m: any) =>
              m.device_name ||
              m.ebs.volume_size !== null ||
              (m.ebs.volume_type ?? "").toString().trim() !== "" ||
              m.ebs.delete_on_termination !== null
          )
      : [];

    return {
      requester_id: this.getRequesterId(),
      launch_justification: (this.form.ec2_launch_justification || "").toString().trim(),
      request_payload: {
        aws_account_id: this.accountId,
        ami_id: this.form.ami_id,
        instance_type: this.form.instance_type,
        key_name: this.form.key_name,
        security_group_ids: this.form.security_group_ids,
        vpc_id: this.form.vpc_id,
        subnet_id: this.form.subnet_id,
        region: this.regionId,
        min_count: 1,
        max_count: 1,
        associate_public_ip_address: this.form.associate_public_ip_address,
        instance_name: this.form.instance_name,
        tags: this.form.tags,
        metadata_options: this.form.metadata_options || {},
        monitoring: this.form.monitoring || {},
        placement: this.form.placement || {},
        platform: this.form.operating_system,
        architecture: this.form.architecture,
        ebs_optimized: this.form.ebs_optimized,
        instance_initiated_shutdown_behavior: this.form.instance_initiated_shutdown_behavior,
        user_data: this.form.user_data,
        iam_instance_profile_arn: this.form.iam_instance_profile_arn,
        ...(mappings.length > 0 ? { block_device_mappings: mappings } : {}),
      },
    };
  }

  addTag() {
    this.form.tags.push({ key: "", value: "" });
  }

  removeTag(index: number) {
    if (!this.form.tags || this.form.tags.length === 0) {
      return;
    }
    if (this.form.tags.length === 1) {
      this.form.tags[0] = { key: "", value: "" };
      return;
    }
    this.form.tags.splice(index, 1);
  }

  addSecurityGroup(id: string) {
    if (id && !this.form.security_group_ids.includes(id))
      this.form.security_group_ids.push(id);
  }

  removeSecurityGroup(id: string) {
    this.form.security_group_ids = this.form.security_group_ids.filter(
      (x: string) => x != id
    );
  }

  submit() {
    this.notifier.alert(
      "Info",
      "",
      "Launch EC2 submit API is not integrated yet.",
      "info",
      3000
    );
  }

  openCreateSecurityGroupForm() {
    this.showCreateSecurityGroupForm = true;
    this.createSecurityGroupStep = 1;
    this.createSecurityGroupSubmitAttempted = false;
    this.createSecurityGroupForm = this.getDefaultCreateSecurityGroupForm(
      this.form?.vpc_id || this.createSecurityGroupForm?.vpc_id || ""
    );
  }

  closeCreateSecurityGroupForm() {
    this.showCreateSecurityGroupForm = false;
    this.createSecurityGroupStep = 1;
    this.createSecurityGroupSubmitAttempted = false;
    this.loading.createSecurityGroup = false;
    this.createSecurityGroupForm = this.getDefaultCreateSecurityGroupForm(
      this.form?.vpc_id || ""
    );
  }

  previousCreateSecurityGroupStep() {
    if (this.createSecurityGroupStep <= 1) {
      return;
    }

    this.createSecurityGroupStep -= 1;
    this.createSecurityGroupSubmitAttempted = false;
  }

  nextCreateSecurityGroupStep() {
    this.createSecurityGroupSubmitAttempted = true;
    if (!this.isCreateSecurityGroupStepValid(this.createSecurityGroupStep)) {
      this.notifier.alert(
        "Warning",
        "",
        this.getCreateSecurityGroupStepValidationMessage(this.createSecurityGroupStep),
        "warning",
        3500
      );
      return;
    }

    if (this.createSecurityGroupStep < this.createSecurityGroupStepLabels.length) {
      this.createSecurityGroupStep += 1;
      this.createSecurityGroupSubmitAttempted = false;
    }
  }

  addCreateSecurityGroupRule(direction: "inbound" | "outbound") {
    const key = direction === "inbound" ? "inbound_rules" : "outbound_rules";
    this.createSecurityGroupForm[key] = [
      ...this.createSecurityGroupForm[key],
      this.createSecurityGroupRule(direction),
    ];
  }

  removeCreateSecurityGroupRule(direction: "inbound" | "outbound", index: number) {
    const key = direction === "inbound" ? "inbound_rules" : "outbound_rules";
    if (!this.createSecurityGroupForm[key]?.length) {
      return;
    }

    if (this.createSecurityGroupForm[key].length === 1) {
      this.createSecurityGroupForm[key] = [this.createSecurityGroupRule(direction)];
      return;
    }

    this.createSecurityGroupForm[key] = this.createSecurityGroupForm[key].filter(
      (_: CreateSecurityGroupRuleRow, rowIndex: number) => rowIndex !== index
    );
  }

  onCreateSecurityGroupRuleProtocolChange(rule: CreateSecurityGroupRuleRow) {
    if (!this.usesCreateSecurityGroupRulePort(rule.protocol)) {
      rule.port = "-";
      return;
    }

    if (!this.isCreateSecurityGroupPortValueValid(rule.port)) {
      rule.port = rule.protocol === "UDP" ? "53" : "443";
    }
  }

  usesCreateSecurityGroupRulePort(protocol: string): boolean {
    return !["all", "-1", "icmp"].includes(
      (protocol || "").toString().trim().toLowerCase()
    );
  }

  isCreateSecurityGroupRuleFlagged(row: CreateSecurityGroupRuleRow): boolean {
    if (!this.isCreateSecurityGroupWorldTarget(row.target)) {
      return false;
    }

    const protocol = (row.protocol || "").toString().trim().toLowerCase();
    if (protocol === "all") {
      return true;
    }

    if (!this.usesCreateSecurityGroupRulePort(protocol)) {
      return false;
    }

    const portRange = this.parseCreateSecurityGroupPortRange(row.port);
    if (!portRange) {
      return false;
    }

    return (
      (portRange.from <= 22 && portRange.to >= 22) ||
      (portRange.from <= 3389 && portRange.to >= 3389)
    );
  }

  isCreateSecurityGroupRuleValid(
    row: CreateSecurityGroupRuleRow,
    direction: "inbound" | "outbound"
  ): boolean {
    const protocol = (row.protocol || "").toString().trim();
    if (!protocol) {
      return false;
    }

    if (
      this.usesCreateSecurityGroupRulePort(protocol) &&
      !this.isCreateSecurityGroupPortValueValid(row.port)
    ) {
      return false;
    }

    if (!this.isCreateSecurityGroupTargetValid(row.target)) {
      return false;
    }

    return !!(row.description || "").toString().trim();
  }

  getCreateSecurityGroupRuleValidationMessage(
    row: CreateSecurityGroupRuleRow,
    direction: "inbound" | "outbound"
  ): string {
    const protocol = (row.protocol || "").toString().trim();
    const targetLabel = direction === "inbound" ? "Source" : "Destination";

    if (!protocol) {
      return "Protocol is required.";
    }

    if (
      this.usesCreateSecurityGroupRulePort(protocol) &&
      !this.isCreateSecurityGroupPortValueValid(row.port)
    ) {
      return "Enter a valid port or port range between 0 and 65535.";
    }

    if (!this.isCreateSecurityGroupTargetValid(row.target)) {
      return `${targetLabel} must be a CIDR block, security group id, or prefix list id.`;
    }

    if (!(row.description || "").toString().trim()) {
      return "Description is required.";
    }

    return "";
  }

  canCreateSecurityGroup(): boolean {
    return !!(
      this.isCreateSecurityGroupStepValid(this.createSecurityGroupStepLabels.length) &&
      !this.loading.createSecurityGroup
    );
  }

  private buildCreateSecurityGroupTagsPayload() {
    const environment = (this.createSecurityGroupForm?.environment || "").toString().trim();
    return environment ? { Environment: environment } : {};
  }

  private getCreateSecurityGroupApiPath() {
    return "apis/ec2-get-master-data/security-groups";
  }

  private getCreateSecurityGroupRulesApiPath(groupId: string) {
    return `apis/ec2-get-master-data/security-groups/${groupId}/rules`;
  }

  private normalizeCreateSecurityGroupProtocol(protocol: string): string {
    const normalizedProtocol = (protocol || "").toString().trim().toLowerCase();
    if (normalizedProtocol === "all") {
      return "-1";
    }
    return normalizedProtocol;
  }

  private getCreateSecurityGroupTargetType(target: string): SecurityGroupRuleTargetType | null {
    const normalizedTarget = (target || "").toString().trim();
    if (!normalizedTarget) {
      return null;
    }

    if (this.isValidSecurityGroupReference(normalizedTarget)) {
      return "referenced_group_ids";
    }

    if (this.isValidPrefixList(normalizedTarget)) {
      return "prefix_list_ids";
    }

    if (normalizedTarget.includes("/")) {
      if (this.isValidCidr(normalizedTarget, "cidr_ipv6")) {
        return "cidr_ipv6";
      }
      if (this.isValidCidr(normalizedTarget, "cidr_ipv4")) {
        return "cidr_ipv4";
      }
    }

    return null;
  }

  private buildSecurityGroupRulePayload(
    row: SecurityGroupRulePayloadRow
  ): SecurityGroupApiRulePayload {
    const normalizedProtocol = this.normalizeCreateSecurityGroupProtocol(row.protocol);
    const normalizedTarget = (row.target_value || "").toString().trim();
    const payload: SecurityGroupApiRulePayload = {
      ip_protocol: normalizedProtocol,
      cidr_ipv4: row.target_type === "cidr_ipv4" ? [normalizedTarget] : [],
      cidr_ipv6: row.target_type === "cidr_ipv6" ? [normalizedTarget] : [],
      prefix_list_ids: row.target_type === "prefix_list_ids" ? [normalizedTarget] : [],
      referenced_group_ids:
        row.target_type === "referenced_group_ids" ? [normalizedTarget] : [],
    };

    if (this.usesCreateSecurityGroupRulePort(normalizedProtocol)) {
      payload.from_port = Number(row.from_port);
      payload.to_port = Number(row.to_port);
    }

    return payload;
  }

  private serializeCreateSecurityGroupRule(
    row: CreateSecurityGroupRuleRow
  ): SecurityGroupApiRulePayload {
    const targetType = this.getCreateSecurityGroupTargetType(row.target);
    const portRange = this.usesCreateSecurityGroupRulePort(row.protocol)
      ? this.parseCreateSecurityGroupPortRange(row.port)
      : null;

    return this.buildSecurityGroupRulePayload({
      protocol: row.protocol,
      from_port: portRange?.from ?? null,
      to_port: portRange?.to ?? null,
      target_type: targetType || "cidr_ipv4",
      target_value: (row.target || "").toString().trim(),
    });
  }

  private buildCreateSecurityGroupRequestPayload() {
    return {
      account_id: this.accountId,
      region: this.regionId,
      group_name: (this.createSecurityGroupForm?.group_name || "").toString().trim(),
      description: (this.createSecurityGroupForm?.description || "").toString().trim(),
      vpc_id: (this.createSecurityGroupForm?.vpc_id || "").toString().trim(),
      tags: this.buildCreateSecurityGroupTagsPayload(),
    };
  }

  private isDefaultAllOutboundRule(row: CreateSecurityGroupRuleRow): boolean {
    const normalizedProtocol = this.normalizeCreateSecurityGroupProtocol(row?.protocol || "");
    const normalizedTarget = (row?.target || "").toString().trim();

    return normalizedProtocol === "-1" && normalizedTarget === "0.0.0.0/0";
  }

  private shouldSkipAuthorizeEgress(): boolean {
    const outboundRules = this.createSecurityGroupForm?.outbound_rules || [];
    return outboundRules.length === 1 && this.isDefaultAllOutboundRule(outboundRules[0]);
  }

  private buildDefaultCreateSecurityGroupEgressRevokePayload(): SecurityGroupApiRulePayload[] {
    return [
      this.buildSecurityGroupRulePayload({
        protocol: "all",
        from_port: null,
        to_port: null,
        target_type: "cidr_ipv4",
        target_value: "0.0.0.0/0",
      }),
    ];
  }

  private buildCreateSecurityGroupRulesUpdatePayload(): CreateSecurityGroupRulesUpdatePayload {
    const shouldSkipAuthorizeEgress = this.shouldSkipAuthorizeEgress();

    return {
      account_id: this.accountId,
      region: this.regionId,
      authorize_ingress: (this.createSecurityGroupForm?.inbound_rules || []).map((row) =>
        this.serializeCreateSecurityGroupRule(row)
      ),
      revoke_ingress: [],
      authorize_egress: shouldSkipAuthorizeEgress
        ? []
        : (this.createSecurityGroupForm?.outbound_rules || []).map((row) =>
            this.serializeCreateSecurityGroupRule(row)
          ),
      revoke_egress: shouldSkipAuthorizeEgress
        ? []
        : this.buildDefaultCreateSecurityGroupEgressRevokePayload(),
    };
  }

  getCreateSecurityGroupVpcLabel(vpcId: string): string {
    const vpc = (this.options.vpcs || []).find((item: any) => item?.vpc_id === vpcId);
    if (!vpc) {
      return vpcId || "-";
    }

    const vpcName = this.normalizeContextValue(vpc?.vpc_name ?? vpc?.name);
    const cidrBlock = this.normalizeContextValue(
      vpc?.cidr_block ?? vpc?.cidr ?? vpc?.ipv4_cidr_block
    );

    if (vpcName && cidrBlock) {
      return `${vpcName} - ${cidrBlock}`;
    }

    if (vpcName) {
      return vpcName;
    }

    if (cidrBlock) {
      return cidrBlock;
    }

    return vpc?.vpc_id || vpcId || "-";
  }

  getCreateSecurityGroupRuleCountLabel(direction: "inbound" | "outbound"): string {
    const count = (direction === "inbound"
      ? this.createSecurityGroupForm?.inbound_rules
      : this.createSecurityGroupForm?.outbound_rules
    )?.length || 0;

    return `${count} rule${count === 1 ? "" : "s"}`;
  }

  formatCreateSecurityGroupRuleForReview(
    row: CreateSecurityGroupRuleRow,
    direction: "inbound" | "outbound"
  ): string {
    const protocol = (row.protocol || "").toString().trim().toUpperCase();
    const port = this.usesCreateSecurityGroupRulePort(row.protocol)
      ? (row.port || "").toString().trim()
      : "-";
    const target = (row.target || "").toString().trim();
    const description = (row.description || "").toString().trim();
    const arrow = direction === "inbound" ? "<-" : "->";

    return `${protocol} ${port} ${arrow} ${target}${description ? ` (${description})` : ""}`;
  }

  private getSecurityGroupOptionId(option: any): string {
    return this.normalizeContextValue(
      option?.security_group_id ?? option?.group_id ?? option?.sg_id ?? option?.id
    );
  }

  private getSecurityGroupOptionName(option: any): string {
    return this.normalizeContextValue(
      option?.security_group_name ?? option?.group_name ?? option?.sg_name ?? option?.name
    );
  }

  private isCreateSecurityGroupStepValid(step: number): boolean {
    switch (step) {
      case 1:
        return this.hasCreateSecurityGroupBasicDetails();
      case 2:
        return this.hasValidCreateSecurityGroupRules("inbound");
      case 3:
        return this.hasValidCreateSecurityGroupRules("outbound");
      case 4:
        return !!(this.createSecurityGroupForm?.environment || "").toString().trim();
      case 5:
        return (
          this.hasCreateSecurityGroupBasicDetails() &&
          this.hasValidCreateSecurityGroupRules("inbound") &&
          this.hasValidCreateSecurityGroupRules("outbound") &&
          !!(this.createSecurityGroupForm?.environment || "").toString().trim()
        );
      default:
        return false;
    }
  }

  private getCreateSecurityGroupStepValidationMessage(step: number): string {
    switch (step) {
      case 1:
        return "Please complete the basic security group details before continuing.";
      case 2:
        return "Please fix the inbound rule details before continuing.";
      case 3:
        return "Please fix the outbound rule details before continuing.";
      case 4:
        return "Please choose an environment tag before continuing.";
      case 5:
        return "Please complete the security group wizard before creating.";
      default:
        return "Please review the security group details.";
    }
  }

  private hasCreateSecurityGroupBasicDetails(): boolean {
    return !!(
      (this.createSecurityGroupForm?.group_name || "").toString().trim() &&
      (this.createSecurityGroupForm?.description || "").toString().trim() &&
      (this.createSecurityGroupForm?.vpc_id || "").toString().trim()
    );
  }

  private hasValidCreateSecurityGroupRules(direction: "inbound" | "outbound"): boolean {
    const rows =
      direction === "inbound"
        ? this.createSecurityGroupForm?.inbound_rules || []
        : this.createSecurityGroupForm?.outbound_rules || [];

    return !!rows.length && rows.every((row: CreateSecurityGroupRuleRow) =>
      this.isCreateSecurityGroupRuleValid(row, direction)
    );
  }

  private isCreateSecurityGroupPortValueValid(port: string): boolean {
    return !!this.parseCreateSecurityGroupPortRange(port);
  }

  private parseCreateSecurityGroupPortRange(port: string) {
    const normalizedPort = (port || "").toString().trim();
    if (!/^\d{1,5}(-\d{1,5})?$/.test(normalizedPort)) {
      return null;
    }

    const [fromRaw, toRaw] = normalizedPort.split("-");
    const from = Number(fromRaw);
    const to = Number(toRaw ?? fromRaw);
    if (
      !Number.isInteger(from) ||
      !Number.isInteger(to) ||
      from < 0 ||
      from > 65535 ||
      to < 0 ||
      to > 65535 ||
      from > to
    ) {
      return null;
    }

    return { from, to };
  }

  private isCreateSecurityGroupTargetValid(value: string): boolean {
    const normalizedValue = (value || "").toString().trim();
    if (!normalizedValue) {
      return false;
    }

    if (this.isValidSecurityGroupReference(normalizedValue)) {
      return true;
    }

    if (this.isValidPrefixList(normalizedValue)) {
      return true;
    }

    if (normalizedValue.includes("/")) {
      return this.isValidCidr(
        normalizedValue,
        normalizedValue.includes(":") ? "cidr_ipv6" : "cidr_ipv4"
      );
    }

    return false;
  }

  private isCreateSecurityGroupWorldTarget(target: string): boolean {
    const normalizedTarget = (target || "").toString().trim();
    return normalizedTarget === "0.0.0.0/0" || normalizedTarget === "::/0";
  }

  private isValidCidr(value: string, type: "cidr_ipv4" | "cidr_ipv6") {
    const normalizedValue = (value || "").toString().trim();
    if (!normalizedValue) {
      return false;
    }

    if (type === "cidr_ipv4") {
      return /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}\/([0-9]|[1-2]\d|3[0-2])$/.test(
        normalizedValue
      );
    }

    return /^[0-9a-fA-F:]+\/([0-9]|[1-9]\d|1[01]\d|12[0-8])$/.test(normalizedValue);
  }

  private isValidSecurityGroupReference(value: string) {
    return /^sg-[a-zA-Z0-9]+$/.test((value || "").toString().trim());
  }

  private isValidPrefixList(value: string) {
    return /^pl-[a-zA-Z0-9]+$/.test((value || "").toString().trim());
  }

  private selectCreatedSecurityGroup(groupId: string, groupName: string) {
    const matched = (this.options.securityGroups || []).find((option: any) => {
      const optionId = this.getSecurityGroupOptionId(option);
      const optionName = this.getSecurityGroupOptionName(option);
      return (groupId && optionId === groupId) || (groupName && optionName === groupName);
    });

    const matchedId = this.getSecurityGroupOptionId(matched);
    if (matchedId && !this.form.security_group_ids.includes(matchedId)) {
      this.form.security_group_ids = [...this.form.security_group_ids, matchedId];
    }
  }

  private buildCreatedSecurityGroupOption(groupId: string) {
    const vpcId = (this.createSecurityGroupForm?.vpc_id || "").toString().trim();
    const vpc = (this.options.vpcs || []).find((item: any) => item?.vpc_id === vpcId);

    return {
      security_group_id: groupId,
      group_id: groupId,
      sg_id: groupId,
      security_group_name: (this.createSecurityGroupForm?.group_name || "").toString().trim(),
      group_name: (this.createSecurityGroupForm?.group_name || "").toString().trim(),
      description: (this.createSecurityGroupForm?.description || "").toString().trim(),
      vpc_id: vpcId,
      vpc_name: this.normalizeContextValue(vpc?.vpc_name ?? vpc?.name),
      cidr_block: this.normalizeContextValue(vpc?.cidr_block ?? vpc?.cidr ?? vpc?.ipv4_cidr_block),
      tags: this.buildCreateSecurityGroupTagsPayload(),
    };
  }

  private hasSecurityGroupOption(groupId: string) {
    return (this.options.securityGroups || []).some(
      (option: any) => this.getSecurityGroupOptionId(option) === groupId
    );
  }

  private async refreshSecurityGroupsAfterCreate(option: any) {
    const vpcId = this.normalizeContextValue(option?.vpc_id);
    if (this.form.vpc_id !== vpcId) {
      this.form.vpc_id = vpcId;
      await this.onVpcChange();
    } else {
      for (let attempt = 0; attempt < 3; attempt++) {
        await this.loadSecurityGroups();
        if (this.hasSecurityGroupOption(this.getSecurityGroupOptionId(option))) {
          break;
        }
        await this.wait(400 * (attempt + 1));
      }
    }

    if (!this.hasSecurityGroupOption(this.getSecurityGroupOptionId(option))) {
      this.options.securityGroups = [option, ...(this.options.securityGroups || [])];
    }
    this.selectCreatedSecurityGroup(
      this.getSecurityGroupOptionId(option),
      this.getSecurityGroupOptionName(option)
    );
  }

  async submitCreateSecurityGroup() {
    this.createSecurityGroupSubmitAttempted = true;
    if (!this.canCreateSecurityGroup()) {
      this.notifier.alert(
        "Warning",
        "",
        "Please complete the required security group details before creating.",
        "warning",
        3000
      );
      return;
    }

    this.loading.createSecurityGroup = true;
    const createResult = await this.postWithPrefixFallback(
      this.getCreateSecurityGroupApiPath(),
      this.buildCreateSecurityGroupRequestPayload()
    );

    const createdGroupId = this.normalizeContextValue(
      createResult?.group_id ?? createResult?.security_group_id ?? createResult?.data?.group_id
    );

    if (!this.isSuccessResponse(createResult) || !createdGroupId) {
      this.loading.createSecurityGroup = false;
      this.notifier.alert(
        "Error",
        "",
        createResult?.error ||
          createResult?.message ||
          createResult?.msg ||
          "Unable to create the security group.",
        "error",
        5000
      );
      return;
    }

    const rulesResult = await this.putWithPrefixFallback(
      this.getCreateSecurityGroupRulesApiPath(createdGroupId),
      this.buildCreateSecurityGroupRulesUpdatePayload()
    );

    if (!this.isSuccessResponse(rulesResult)) {
      const createdSecurityGroup = this.buildCreatedSecurityGroupOption(createdGroupId);
      await this.refreshSecurityGroupsAfterCreate(createdSecurityGroup);
      this.loading.createSecurityGroup = false;
      this.closeCreateSecurityGroupForm();
      this.notifier.alert(
        "Warning",
        "",
        rulesResult?.error ||
          rulesResult?.message ||
          rulesResult?.msg ||
          `Security group ${createdGroupId} was created, but applying the rules failed.`,
        "warning",
        7000
      );
      return;
    }

    const createdSecurityGroup = this.buildCreatedSecurityGroupOption(createdGroupId);
    await this.refreshSecurityGroupsAfterCreate(createdSecurityGroup);
    this.loading.createSecurityGroup = false;
    this.closeCreateSecurityGroupForm();
    this.notifier.alert(
      "Success",
      "",
      rulesResult?.message || rulesResult?.msg || "Security group created successfully.",
      "success",
      4000
    );
  }
}
